import type { ReactElement } from "react";
import { Button, Stack, Text } from "@ux/primitives";
import { palette, space } from "@ux/tokens";

export interface BulkActionBarProps {
    readonly count: number;
    readonly busy: boolean;
    readonly error: string | null;
    readonly onAssign: () => void;
    readonly onAvailable: () => void;
    readonly onDelete: () => void;
    readonly onClear: () => void;
}

/**
 * Action strip shown when the operator has checked one or more assets
 * or workspaces. "Available" releases the current assignment so the
 * record stays in the catalog and shows as available (the Preserve /
 * available outcome).
 */
export function BulkActionBar({
    count,
    busy,
    error,
    onAssign,
    onAvailable,
    onDelete,
    onClear,
}: BulkActionBarProps): ReactElement | null {
    if (count === 0) return null;

    return (
        <div
            style={{
                padding: `${space[3]} ${space[4]}`,
                borderBottom: `1px solid ${palette.border}`,
                background: palette.accentBg,
            }}
        >
            <Stack gap={2}>
                <Stack direction="row" align="center" justify="space-between" gap={2} wrap>
                    <Text role="body" weight="semibold">
                        {count === 1 ? "1 selected" : `${count} selected`}
                    </Text>
                    <Stack direction="row" gap={2} wrap>
                        <Button variant="primary" size="sm" onClick={onAssign} disabled={busy}>
                            Assign
                        </Button>
                        <Button variant="secondary" size="sm" onClick={onAvailable} disabled={busy}>
                            Available
                        </Button>
                        <Button variant="danger" size="sm" onClick={onDelete} disabled={busy}>
                            Delete
                        </Button>
                        <Button variant="ghost" size="sm" onClick={onClear} disabled={busy}>
                            Clear
                        </Button>
                    </Stack>
                </Stack>
                {error !== null ? (
                    <Text role="muted" color={palette.danger}>
                        {error}
                    </Text>
                ) : null}
            </Stack>
        </div>
    );
}
